import type React from "react";

import { Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import type { CareEvent } from "api/models/CareEvent";
import { useProfileContext } from "contexts/ProfileContext";
import { RecordNoteHeader } from "routes/patients/PatientProfile/components/Information/components/RecordNoteHeader";
import { MUIAccordion, MUIAccordionDetails, MUIAccordionSummary } from "shared/atoms/MUIAccordion";
import type { MedicalNoteType } from "types/MedicalNoteConfig";
import { useLocalizedDate } from "utils/date";
import { isKickOffNote } from "utils/medicalNotes";
import { userIsPartner } from "utils/profile/profileHelper";
import { translateHealthCareProfessionalType, translateMedicalNoteType } from "utils/translations";

import { Addenda } from "../Addenda";
import { PDFDownloadButton } from "../PDFDownload";

interface Props {
  record: CareEvent;
}

export const RecordNote: React.FC<Props> = ({ record }) => {
  const {
    id,
    start_time,
    label,
    health_care_professional_name,
    health_care_professional_type,
    confirmed_identity,
    structured_fields,
    problem,
    medical_note_addenda,
    medical_record_note,
    general_condition,
    communication_type,
  } = record;
  const { t } = useTranslation();
  const { profile } = useProfileContext();
  const isPartner = userIsPartner(profile);
  const { format, parseISO, add } = useLocalizedDate();
  const renderDuration = (days: number | undefined | null): string => {
    if (typeof days === "number") {
      const expiryDate = start_time ? format(add(parseISO(start_time), { days }), "PP") : "";
      const durationAndExpiryDate = t("record_extended_note.duration", {
        days: days?.toString(),
        date: expiryDate,
      });
      return durationAndExpiryDate;
    }
    return t("patients.initial_evaluation.no_duration");
  };

  const renderSection = (title?: string, content?: string | null) => (
    <Stack spacing={0.5}>
      {title && (
        <Typography variant="subtitle1" color="textSecondary">
          {title}
        </Typography>
      )}
      <Typography>{content ?? "-"}</Typography>
    </Stack>
  );

  return (
    <MUIAccordion>
      <MUIAccordionSummary>
        <RecordNoteHeader date={start_time}>
          {translateMedicalNoteType(record.label.toUpperCase() as MedicalNoteType)}
        </RecordNoteHeader>
      </MUIAccordionSummary>
      <MUIAccordionDetails>
        <Stack spacing={2}>
          {isPartner && <PDFDownloadButton record={record} />}
          <Row>
            {renderSection(t("patients.medical_records.responsible"), health_care_professional_name)}
            {renderSection(
              t("patients.medical_records.role"),
              translateHealthCareProfessionalType(health_care_professional_type)
            )}
          </Row>
          {!confirmed_identity && (
            <Row>{renderSection(t("patients.notes.fields.confirmed_identity"), t("buttons.yes"))}</Row>
          )}
          <Row>{renderSection(t("patients.medical_records.problem"), problem)}</Row>
          <Row>{renderSection(t("patients.medical_records.note"), medical_record_note)}</Row>
          <Row>{renderSection(t("patients.medical_records.general_health"), general_condition)}</Row>
          {communication_type && (
            <Row>
              {renderSection(
                t("patients.notes.fields.communication_type"),
                t(`patients.notes.communication_type_dropdown.options.${communication_type}`)
              )}
            </Row>
          )}
          {isKickOffNote(label) && (
            <Row>
              {renderSection(t("patients.initial_evaluation.frequency"), t("patients.initial_evaluation.daily"))}
              {renderSection(
                t("patients.initial_evaluation.duration"),
                renderDuration(structured_fields.plan_duration_days)
              )}
            </Row>
          )}

          {!isPartner && <Addenda careEventId={id} medical_note_addenda={medical_note_addenda} />}
        </Stack>
      </MUIAccordionDetails>
    </MUIAccordion>
  );
};

const Row = styled.div`
  display: flex;
  gap: 24px;
  justify-content: flex-start;
  flex-wrap: wrap;
`;
